import { zodTransformFirestoreTimestampToDate } from "../../utils/zodUtils.mjs";
import { zodFlavorId } from "../ids.mjs";
import { defineFirestoreCollection } from "../firebaseCollectionSchema.mjs";
import { bookingDemographicsSchema } from "./bookingDemographics.mjs";
import { bridgeMappedNoteFieldKeySchema } from "../vendors/bridge/mappedNote.mjs";
import { z as z$1 } from "zod";

// src/models/consultation/consultation.ts
const firestoreDate = z$1.any().transform(zodTransformFirestoreTimestampToDate);
const consultationLifecycleStatusWriteSchema = z$1.enum([
	"draft",
	"reserved",
	"ensuring_bridge_service",
	"payment_pending",
	"provisioning",
	"scheduled",
	"ensuring_bridge_billing",
	"bridge_settling",
	"bridge_triage",
	"settlement_triage",
	"pending_settlement_payment",
	"completed",
	"pending_cancellation",
	"pending_noshow_fee",
	"cancelled",
	"cancel_failed"
]);
/** Legacy Firestore rows may still carry removed statuses; coerce at read — ADR-DOM-002. */
const consultationLifecycleStatusSchema = z$1.preprocess((value) => {
	if (value === "expired") return "cancelled";
	if (value === "insurance_verify") return "draft";
	return value;
}, consultationLifecycleStatusWriteSchema);
const cancellationReasonSchema = z$1.enum([
	"customer_aborted",
	"hold_expired",
	"admin_cancelled",
	"payment_abandoned",
	"draft_ttl",
	"demographics_mismatch",
	"state_unsupported",
	"superseded",
	"canvas_driven",
	"canvas_no_show"
]);
const cancellationFailureSchema = z$1.enum([
	"stripe_failure",
	"canvas_failure",
	"tellescope_calendar_failure",
	"bridge_service_failure",
	"slot_hold_failure"
]);
const cancellationSchema = z$1.object({
	reason: cancellationReasonSchema,
	failures: z$1.array(cancellationFailureSchema).optional(),
	/** Slots still blocking exit from pending_cancellation. Cleared on terminal exit. */
	pendingSlots: z$1.array(cancellationFailureSchema).optional(),
	/**
	* Set once when cancel pending gates observe Tellescope calendar unwind final
	* (success or intentional skip). Drives slot occupancy post-filter (R-08).
	*/
	tellescopeCalendarClearedAt: firestoreDate.optional(),
	/** True when cancel intent is inside the 24h crystallization window. */
	inWindow: z$1.boolean().optional(),
	/** Money branch: drives pending_noshow_fee after unwind. */
	feeOwed: z$1.boolean().optional(),
	feeWaived: z$1.boolean().optional(),
	feeCents: z$1.number().int().positive().optional(),
	classifiedAt: firestoreDate.optional()
});
const consultationOperationalStatusSchema = z$1.enum([
	"draft",
	"scheduled",
	"in_progress",
	"completed",
	"no_show",
	"booking_cancelled",
	"cancelled",
	"rescheduled"
]);
const consultationModalitySchema = z$1.enum(["phone", "video"]);
/** Product feature that owns a consultation row (L1 registry discriminator). */
const consultationFeatureKeySchema = z$1.enum(["clinical_consultation"]);
/** Visit type within a feature's L1 registry. */
const consultationConsultTypeSchema = z$1.enum(["clinical_main", "clinical_followup"]);
function coerceLegacyExpiredConsultation(input) {
	if (typeof input !== "object" || input === null) return input;
	const doc = input;
	if (doc.lifecycleStatus !== "expired") return input;
	const cancellation = typeof doc.cancellation === "object" && doc.cancellation !== null ? doc.cancellation : void 0;
	return {
		...doc,
		lifecycleStatus: "cancelled",
		cancellation: {
			reason: "hold_expired",
			...cancellation?.failures != null ? { failures: cancellation.failures } : {}
		}
	};
}
const consultationDocumentSchema = z$1.object({
	id: zodFlavorId(),
	customerId: zodFlavorId(),
	consultantId: zodFlavorId().optional(),
	supersedesConsultationId: zodFlavorId().optional(),
	supersededByConsultationId: zodFlavorId().optional(),
	featureKey: consultationFeatureKeySchema,
	consultType: consultationConsultTypeSchema,
	lifecycleStatus: consultationLifecycleStatusSchema,
	operationalStatus: consultationOperationalStatusSchema,
	scheduledStart: firestoreDate.optional(),
	scheduledEnd: firestoreDate.optional(),
	durationMinutes: z$1.number().int().positive().optional(),
	modality: consultationModalitySchema.optional(),
	stateAtBooking: z$1.string().optional(),
	/** Visit state validated during insured insurance verify (Bridge SE target). */
	bridgeEligibilityState: z$1.string().optional(),
	/** Insured vs self-pay booking path (customer projection). */
	billingPath: z$1.enum(["insured", "self_pay"]).optional(),
	/** Admin follow-up one-pager: hide from customer hub until checkout method is chosen. */
	bookingHiddenOnCustomerUi: z$1.boolean().optional(),
	/** Set when admin `releaseToPatientCheckout` hands off to patient checkout (care-team marker). */
	careTeamCheckoutReleasedAt: firestoreDate.optional(),
	/** True when Bridge mapped note v1 keys are complete after evaluate (admin queue). */
	bridgeNoteReady: z$1.boolean().optional(),
	/** Missing v1 mapped field names only — no chart values (admin queue). */
	bridgeNoteMissingFields: z$1.array(bridgeMappedNoteFieldKeySchema).optional(),
	/** Last Bridge note POST rejection. Field name and validation type only. */
	bridgeNotePostError: z$1.string().nullish(),
	/** Demographics + state captured at consultation create for resume validity. */
	bookingDemographics: bookingDemographicsSchema.optional(),
	/** True when this document was created as the signup booking intro draft. */
	signupBooking: z$1.literal(true).optional(),
	/** Our `bridge_service_eligibilities` sync id for insured slot estimate. */
	bridgeServiceEligibilityId: z$1.string().optional(),
	checkoutExpiresAt: firestoreDate.optional(),
	reservedSlotKey: z$1.string().min(1).optional(),
	tellescopeUserId: z$1.string().min(1).optional(),
	startTimeInMS: z$1.number().int().nonnegative().optional(),
	reservedAt: firestoreDate.optional(),
	/** Set when cancel enters pending_cancellation (intent time). */
	cancellation: cancellationSchema.optional(),
	confirmedAt: firestoreDate.optional(),
	completedAt: firestoreDate.optional(),
	cancelledAt: firestoreDate.optional(),
	createdAt: firestoreDate,
	updatedAt: firestoreDate
});
/** Parses Firestore rows including legacy `expired` lifecycle (ADR-DOM-002). */
const consultationSchema = z$1.preprocess(coerceLegacyExpiredConsultation, consultationDocumentSchema);
const Consultations = defineFirestoreCollection("consultations", consultationDocumentSchema, void 0, {
	dwReplication: true,
	firestoreCollectionPath: "consultations"
});

export { Consultations, cancellationFailureSchema, cancellationReasonSchema, cancellationSchema, consultationConsultTypeSchema, consultationFeatureKeySchema, consultationLifecycleStatusSchema, consultationModalitySchema, consultationOperationalStatusSchema, consultationSchema };
//# sourceMappingURL=consultation.mjs.map